/**
 * assets/carousel-init.js
 * Global Video Hub — Carousel Controller
 *
 * Responsibilities:
 * 1. Initialize every UGC Splide carousel independently
 * 2. Bind custom Previous / Next navigation
 * 3. Keep navigation disabled states in sync
 * 4. Support multiple Theme Block instances
 * 5. Support Shopify Theme Editor dynamic reloads
 * 6. Prevent duplicate Splide initialization
 * 7. Play visible muted MP4 card previews only
 * 8. Pause previews when hidden / off-screen
 * 9. Respect prefers-reduced-motion
 * 10. Fall back to native horizontal scrolling if Splide is unavailable
 */

(function () {
  'use strict';

  const ROOT_SELECTOR = '[data-ugc-carousel-root]';
  const SPLIDE_SELECTOR = '.ugc-splide';
  const CARD_SELECTOR = '.ugc-video-card';
  const PREVIEW_SELECTOR = '.ugc-card-video-preview';

  const initializedRoots = new WeakSet();
  const splideInstances = new Map();

  const reduceMotionQuery = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

  const previewObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(handlePreviewIntersection, {
        root: null,
        rootMargin: '50px 0px',
        threshold: 0.55,
      })
    : null;


  /* =========================================================
     Bootstrap
  ========================================================= */

  function initAll(scope) {
    var context = scope || document;

    if (context.matches && context.matches(ROOT_SELECTOR)) {
      initCarousel(context);
    }

    var roots = context.querySelectorAll
      ? context.querySelectorAll(ROOT_SELECTOR)
      : [];

    for (var i = 0; i < roots.length; i++) {
      initCarousel(roots[i]);
    }
  }


  function initCarousel(root) {
    if (!root || initializedRoots.has(root)) return;

    var splideEl = root.querySelector(SPLIDE_SELECTOR);

    if (!splideEl) return;

    initializedRoots.add(root);

    setupVideoPreviews(root);

    if (typeof window.Splide !== 'function') {
      enableNativeFallback(root, splideEl);
      return;
    }

    createSplide(root, splideEl);
  }


  /* =========================================================
     Splide
  ========================================================= */

  function createSplide(root, splideEl) {
    var blockId = splideEl.dataset.splideBlockId || '';

    var prevButton = blockId
      ? document.getElementById('ugc-prev-' + blockId)
      : root.querySelector('.ugc-nav-prev');

    var nextButton = blockId
      ? document.getElementById('ugc-next-' + blockId)
      : root.querySelector('.ugc-nav-next');

    var options = readSplideOptions(splideEl);

    /*
     * JS defaults are intentionally repeated even though Liquid
     * supplies data-splide. This prevents a malformed attribute
     * from breaking the component completely.
     */
    var finalOptions = Object.assign(
      {
        autoWidth: true,
        gap: '10px',
        pagination: false,
        arrows: false,
        drag: true,
        snap: true,
        rewind: false,
        speed: prefersReducedMotion() ? 0 : 400,
        flickPower: 450,
        waitForTransition: false,
      },
      options
    );

    if (prefersReducedMotion()) {
      finalOptions.speed = 0;
    }

    var splide;

    try {
      splide = new window.Splide(splideEl, finalOptions);
    } catch (error) {
      console.warn('[UGC Carousel] Splide creation failed:', error);
      enableNativeFallback(root, splideEl);
      return;
    }

    var updateNavigation = function () {
      updateNavState(splide, prevButton, nextButton);
    };

    splide.on('mounted', updateNavigation);
    splide.on('moved', updateNavigation);
    splide.on('scrolled', updateNavigation);
    splide.on('resized', updateNavigation);
    splide.on('refresh', updateNavigation);
    splide.on('updated', updateNavigation);

    try {
      splide.mount();
    } catch (error) {
      console.warn('[UGC Carousel] Splide mount failed:', error);

      try {
        splide.destroy(true);
      } catch (_) {
        // No-op.
      }

      enableNativeFallback(root, splideEl);
      return;
    }

    splideInstances.set(root, splide);

    if (prevButton) {
      prevButton.addEventListener('click', function () {
        splide.go('<');
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', function () {
        splide.go('>');
      });
    }

    updateNavigation();
  }


  function readSplideOptions(splideEl) {
    var raw = splideEl.getAttribute('data-splide');

    if (!raw) return {};

    try {
      return JSON.parse(raw);
    } catch (error) {
      console.warn('[UGC Carousel] Invalid data-splide JSON:', error);
      return {};
    }
  }


  /* =========================================================
     Navigation state
  ========================================================= */

  function updateNavState(splide, prevButton, nextButton) {
    if (!splide) return;

    var length = splide.length || 0;

    if (length <= 1) {
      setButtonDisabled(prevButton, true);
      setButtonDisabled(nextButton, true);
      return;
    }

    var index = splide.index;

    var controller = splide.Components && splide.Components.Controller
      ? splide.Components.Controller
      : null;

    var endIndex = 0;

    if (controller && typeof controller.getEnd === 'function') {
      endIndex = controller.getEnd();
    } else {
      endIndex = Math.max(0, length - 1);
    }

    var canGoPrevious = index > 0;
    var canGoNext = index < endIndex;

    /*
     * In certain autoWidth layouts Splide's calculated end can
     * become zero because the complete list fits inside the track.
     */
    if (endIndex <= 0) {
      setButtonDisabled(prevButton, true);
      setButtonDisabled(nextButton, true);
      return;
    }

    setButtonDisabled(prevButton, !canGoPrevious);
    setButtonDisabled(nextButton, !canGoNext);
  }


  function setButtonDisabled(button, disabled) {
    if (!button) return;

    button.disabled = Boolean(disabled);
    button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  }


  /* =========================================================
     MP4 card previews
  ========================================================= */

  function setupVideoPreviews(root) {
    var previews = root.querySelectorAll(PREVIEW_SELECTOR);

    for (var i = 0; i < previews.length; i++) {
      var video = previews[i];

      /*
       * Defensive enforcement. Card previews must never create
       * audio while a shopper is browsing the PDP.
       */
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;

      if (prefersReducedMotion()) {
        pausePreview(video);
        continue;
      }

      if (previewObserver) {
        previewObserver.observe(video);
      }
    }
  }


  function handlePreviewIntersection(entries) {
    if (prefersReducedMotion()) {
      for (var i = 0; i < entries.length; i++) {
        pausePreview(entries[i].target);
      }
      return;
    }

    for (var j = 0; j < entries.length; j++) {
      var entry = entries[j];
      var video = entry.target;

      if (!(video instanceof HTMLVideoElement)) continue;

      if (
        entry.isIntersecting &&
        entry.intersectionRatio >= 0.55 &&
        !document.hidden
      ) {
        playPreview(video);
      } else {
        pausePreview(video);
      }
    }
  }


  function playPreview(video) {
    if (!video || document.hidden || prefersReducedMotion()) return;

    video.muted = true;

    var promise;

    try {
      promise = video.play();
    } catch (_) {
      return;
    }

    if (promise && typeof promise.catch === 'function') {
      promise.catch(function () {
        /*
         * Browser autoplay policies can reject play().
         * This is non-fatal: the card still behaves as a static
         * video frame and remains clickable.
         */
      });
    }
  }


  function pausePreview(video) {
    if (!video) return;

    try {
      video.pause();
    } catch (_) {
      // No-op.
    }
  }


  function pauseAllPreviews(scope) {
    var context = scope || document;

    var videos = context.querySelectorAll
      ? context.querySelectorAll(PREVIEW_SELECTOR)
      : [];

    for (var i = 0; i < videos.length; i++) {
      pausePreview(videos[i]);
    }
  }


  /* =========================================================
     Document visibility
  ========================================================= */

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      pauseAllPreviews(document);
      return;
    }

    if (prefersReducedMotion()) return;

    /*
     * IntersectionObserver will normally restore playback on a
     * new intersection event. Some browsers do not emit one
     * immediately after returning to the tab, so check viewport
     * visibility manually.
     */
    var videos = document.querySelectorAll(PREVIEW_SELECTOR);

    for (var i = 0; i < videos.length; i++) {
      var video = videos[i];

      if (isMostlyVisible(video)) {
        playPreview(video);
      }
    }
  });


  function isMostlyVisible(element) {
    if (!element || !element.getBoundingClientRect) return false;

    var rect = element.getBoundingClientRect();

    if (rect.width <= 0 || rect.height <= 0) return false;

    var viewportHeight =
      window.innerHeight || document.documentElement.clientHeight;

    var viewportWidth =
      window.innerWidth || document.documentElement.clientWidth;

    var visibleTop = Math.max(rect.top, 0);
    var visibleBottom = Math.min(rect.bottom, viewportHeight);
    var visibleLeft = Math.max(rect.left, 0);
    var visibleRight = Math.min(rect.right, viewportWidth);

    var visibleHeight = Math.max(0, visibleBottom - visibleTop);
    var visibleWidth = Math.max(0, visibleRight - visibleLeft);

    var visibleArea = visibleWidth * visibleHeight;
    var totalArea = rect.width * rect.height;

    if (!totalArea) return false;

    return visibleArea / totalArea >= 0.55;
  }


  /* =========================================================
     Reduced Motion
  ========================================================= */

  function prefersReducedMotion() {
    return Boolean(reduceMotionQuery && reduceMotionQuery.matches);
  }


  function handleReducedMotionChange(event) {
    if (event.matches) {
      pauseAllPreviews(document);
    }

    /*
     * Existing Splide instances are updated instead of destroyed.
     */
    splideInstances.forEach(function (splide) {
      if (!splide || typeof splide.options === 'undefined') return;

      try {
        splide.options = {
          speed: event.matches ? 0 : 400,
        };
      } catch (_) {
        // Older Splide versions may not allow runtime assignment.
      }
    });
  }


  if (reduceMotionQuery) {
    if (typeof reduceMotionQuery.addEventListener === 'function') {
      reduceMotionQuery.addEventListener(
        'change',
        handleReducedMotionChange
      );
    } else if (typeof reduceMotionQuery.addListener === 'function') {
      /*
       * Safari compatibility.
       */
      reduceMotionQuery.addListener(handleReducedMotionChange);
    }
  }


  /* =========================================================
     Native fallback

     If Splide fails or has not been loaded, the content must
     remain usable instead of becoming a broken component.
  ========================================================= */

  function enableNativeFallback(root, splideEl) {
    if (!root || !splideEl) return;

    root.classList.add('ugc-carousel-native');

    var track = splideEl.querySelector('.splide__track');

    if (!track) return;

    var blockId = splideEl.dataset.splideBlockId || '';

    var prevButton = blockId
      ? document.getElementById('ugc-prev-' + blockId)
      : root.querySelector('.ugc-nav-prev');

    var nextButton = blockId
      ? document.getElementById('ugc-next-' + blockId)
      : root.querySelector('.ugc-nav-next');

    function scrollAmount() {
      return Math.max(130, Math.round(track.clientWidth * 0.75));
    }

    function updateFallbackNavigation() {
      var maxScroll = Math.max(
        0,
        track.scrollWidth - track.clientWidth
      );

      var position = Math.max(0, track.scrollLeft);

      setButtonDisabled(
        prevButton,
        position <= 2
      );

      setButtonDisabled(
        nextButton,
        position >= maxScroll - 2
      );
    }

    if (prevButton) {
      prevButton.addEventListener('click', function () {
        track.scrollBy({
          left: -scrollAmount(),
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', function () {
        track.scrollBy({
          left: scrollAmount(),
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
      });
    }

    track.addEventListener(
      'scroll',
      throttleWithAnimationFrame(updateFallbackNavigation),
      { passive: true }
    );

    window.addEventListener(
      'resize',
      throttleWithAnimationFrame(updateFallbackNavigation),
      { passive: true }
    );

    updateFallbackNavigation();
  }


  /* =========================================================
     Shopify Theme Editor

     Theme Blocks / Sections can be dynamically replaced without
     a full page refresh.
  ========================================================= */

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });


  document.addEventListener('shopify:block:select', function (event) {
    var target = event.target;

    if (!target) return;

    var root = target.matches && target.matches(ROOT_SELECTOR)
      ? target
      : target.querySelector
        ? target.querySelector(ROOT_SELECTOR)
        : null;

    if (!root) return;

    initCarousel(root);

    /*
     * When the merchant selects the block in Theme Editor,
     * make sure it is visible.
     */
    try {
      root.scrollIntoView({
        block: 'nearest',
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    } catch (_) {
      // Older browsers.
    }
  });


  document.addEventListener('shopify:section:unload', function (event) {
    destroyWithin(event.target);
  });


  function destroyWithin(scope) {
    if (!scope) return;

    var roots = [];

    if (scope.matches && scope.matches(ROOT_SELECTOR)) {
      roots.push(scope);
    }

    if (scope.querySelectorAll) {
      var descendants = scope.querySelectorAll(ROOT_SELECTOR);

      for (var i = 0; i < descendants.length; i++) {
        roots.push(descendants[i]);
      }
    }

    for (var j = 0; j < roots.length; j++) {
      destroyCarousel(roots[j]);
    }

    if (scope.querySelectorAll) {
      var videos = scope.querySelectorAll(PREVIEW_SELECTOR);

      for (var k = 0; k < videos.length; k++) {
        if (previewObserver) {
          previewObserver.unobserve(videos[k]);
        }

        pausePreview(videos[k]);
      }
    }
  }


  function destroyCarousel(root) {
    var splide = splideInstances.get(root);

    if (splide) {
      try {
        splide.destroy(true);
      } catch (_) {
        // No-op.
      }

      splideInstances.delete(root);
    }
  }


  /* =========================================================
     Utility
  ========================================================= */

  function throttleWithAnimationFrame(callback) {
    var frame = null;

    return function () {
      var context = this;
      var args = arguments;

      if (frame !== null) return;

      frame = window.requestAnimationFrame(function () {
        frame = null;
        callback.apply(context, args);
      });
    };
  }


  /* =========================================================
     Initial start
  ========================================================= */

  function start() {
    initAll(document);
  }


  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, {
      once: true,
    });
  } else {
    start();
  }

})();